import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getAuthenticatedLandingPath, useAuth } from "@/lib/auth.jsx";

export default function Login() {
    const { user, loading, login } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!loading && user) {
            navigate(getAuthenticatedLandingPath(user), { replace: true });
        }
    }, [loading, navigate, user]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;

        setSubmitting(true);
        try {
            const user = await login(email.trim(), password);
            toast.success("Welcome back");
            navigate(getAuthenticatedLandingPath(user));
        } catch (err) {
            toast.error(err.userMessage || "Login failed. Check your credentials and try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[hsl(var(--background))] text-sm text-slate-500" role="status">
                Checking session…
            </div>
        );
    }

    return (
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
            <div className="relative overflow-hidden bg-[#0F2A47] text-white p-10 lg:p-14 flex flex-col justify-between">
                <div className="absolute inset-0 coastal-wash opacity-50" />
                <div className="absolute inset-0 noise opacity-40" />
                <div className="relative z-10">
                    <div className="flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-md bg-white p-1.5 shadow-sm">
                            <img src="/ehs-logo.webp" alt="Easy HomeSource" className="h-full w-full object-contain" />
                        </div>
                        <div>
                            <div className="font-display text-2xl">Easy HomeSource</div>
                            <div className="text-xs uppercase tracking-widest text-white/60">
                                Internal Operating System
                            </div>
                        </div>
                    </div>
                </div>

                <div className="relative z-10 max-w-lg">
                    <h1 className="font-display text-4xl lg:text-5xl leading-tight">
                        One workspace for quotes, customers, homes, and sales workflow.
                    </h1>
                    <p className="mt-6 text-white/70 text-base leading-relaxed">
                        Access the Easy HomeSource employee portal to manage customer records,
                        build professional home quotes, and track the sales process from first
                        conversation through fulfillment.
                    </p>
                </div>

                <div className="relative z-10 text-xs text-white/50">
                    © {new Date().getFullYear()} Easy HomeSource — 9011 McIntyre Rd, Brooksville, FL
                </div>
            </div>

            <div className="flex items-center justify-center bg-[hsl(var(--background))] p-6 lg:p-12">
                <div className="w-full max-w-md">
                    <div className="mb-6">
                        <h2 className="font-display text-3xl text-slate-900">Employee sign in</h2>
                        <p className="text-sm text-slate-500 mt-1">
                            Use your authorized Easy HomeSource credentials to continue.
                        </p>
                        {searchParams.get("ghl") === "unlinked" && (
                            <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950" role="alert">
                                Your GHL account is not linked to an EHS Quote System user. Contact an admin.
                                You can still sign in with your portal credentials below.
                            </div>
                        )}
                    </div>

                    <form onSubmit={onSubmit} className="space-y-4">
                        <div>
                            <Label htmlFor="email" className="text-sm">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@easyhomesource.com"
                                className="mt-1"
                                required
                                data-testid="login-form-email-input"
                            />
                        </div>

                        <div>
                            <Label htmlFor="password" className="text-sm">Password</Label>
                            <div className="relative mt-1">
                                <Input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="pr-12"
                                    required
                                    data-testid="login-form-password-input"
                                />
                                <button
                                    type="button"
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                    aria-pressed={showPassword}
                                    onClick={() => setShowPassword((current) => !current)}
                                    className="absolute inset-y-0 right-0 z-10 flex w-12 items-center justify-center rounded-r-md text-slate-500 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0F2A47] focus:ring-offset-2"
                                    data-testid="login-form-toggle-password-visibility-button"
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            disabled={submitting}
                            className="w-full bg-[#0F2A47] hover:bg-[#14385E] text-white h-11 disabled:cursor-not-allowed disabled:opacity-75"
                            data-testid="login-form-submit-button"
                        >
                            {submitting ? "Signing in…" : (
                                <>
                                    Sign in <ArrowRight className="ml-2 h-4 w-4" />
                                </>
                            )}
                        </Button>
                    </form>

                    <div className="mt-8 rounded-lg border border-slate-200 bg-white p-4">
                        <div className="flex items-start gap-3">
                            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#0F2A47]/5 text-[#0F2A47]">
                                <ShieldCheck className="h-4 w-4" />
                            </div>
                            <div>
                                <div className="text-sm font-semibold text-slate-900">
                                    Authorized team access only
                                </div>
                                <p className="mt-1 text-sm leading-relaxed text-slate-500">
                                    This portal is for Easy HomeSource team members. Contact an
                                    administrator if you need an account or cannot access your login.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
